import { GenerationType } from 'components/BlazeCommandLine/api/createDocGenerationRequest'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { uniq } from 'lodash'
import { WizardCardMap, LevelCardMap, WizardOrLevelCard, Wizard, WizardCard } from './Types'
import { DesignChannel } from 'components/NewFileModal/Types'
import { TurnInto } from 'types'

/**
 * GENERAL
 */
export const RECENT_WIZARDS_MAX_COUNT = 10

export const RECENT_WIZARDS_LEVEL_1_MAX_CARDS = 4

export const FOR_YOU_RECENT_WIZARDS_FILE_THRESHOLD = 5

/**
 * LEVELS
 */
export const LEVELS_2 = [
  'social_media',
  'marketplace',
  'video',
  'blogs_seo',
  'brainstorm_ideas',
  'ad_copy',
  'recently_used',
] as const

export const LEVELS_3_SOCIAL = [
  'instagram',
  'facebook',
  'linkedin',
  'tiktok',
  'twitter',
  'youtube',
  'pinterest',
  'whatsapp',
] as const
export const LEVELS_3_MARKETPLACE = ['product_copy', 'product_visual'] as const
export const LEVELS_3_VIDEO = ['tiktok', 'youtube', 'facebook', 'instagram'] as const
export const LEVELS_3_ADVANCED = ['turn_into'] as const
export const LEVELS_3_EMAILS = ['newsletter', 'lifecycle', 'ecommerce', 'email_sales'] as const
export const LEVELS_3_OTHER = ['websites', 'sales', 'customer_support', 'recruiting'] as const

export const LEVELS_3 = uniq([
  ...LEVELS_3_SOCIAL,
  ...LEVELS_3_MARKETPLACE,
  ...LEVELS_3_VIDEO,
  ...LEVELS_3_ADVANCED,
  ...LEVELS_3_EMAILS,
  ...LEVELS_3_OTHER,
])

export const LEVELS_4 = ['linkedin_post', 'twitter_post'] as const

export const ALL_LEVELS = ['create_new', ...LEVELS_2, ...LEVELS_3, ...LEVELS_4]

/**
 * CARD DEFINITIONS FOR EACH LEVEL
 */

type LevelCategories = Record<string, WizardOrLevelCard[]>

type Levels = {
  L1: LevelCategories
  L2: LevelCategories
  L3: LevelCategories
  L4: LevelCategories
}

export const LEVEL_CARDS: Levels = {
  L1: {
    FOR_YOU_INEXPERIENCED: [
      {
        title: 'Generate Content Ideas',
        description: 'Blaze gives you the ideas you need to start marketing your business.',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1740075744/blaze_assets/mobile_home_and_create_new/create_new/brainstorm-ideas_lhvi8r.png',
        wizard: GenerationType.Brainstorm,
      },
    ],
    FOR_YOU_EXPERIENCED: [
      {
        title: 'Multiple Instagram Posts',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741712596/blaze_assets/mobile_home_and_create_new/L3/multiple-instagram-posts_eqa3d1.png',
        wizard: GenerationType.InstagramPlanning,
      },
      {
        title: 'Multiple LinkedIn Posts',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741712854/blaze_assets/mobile_home_and_create_new/L3/multiple-facebook-posts_xp3mp7.png',
        wizard: GenerationType.LinkedInPlanning,
        imgScale: 0.5,
      },
      {
        title: 'Turn Video into Content',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741025624/blaze_assets/mobile_home_and_create_new/create_new/turn-video-into_bfcrpi.png',
        wizard: TurnInto.VideoIntoContent,
      },
      {
        title: 'Turn Audio into Content',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741025623/blaze_assets/mobile_home_and_create_new/create_new/turn-audio-into_ctyea1.png',
        wizard: TurnInto.AudioIntoContent,
      },
    ],
    SOCIAL_MEDIA: [
      {
        title: 'Instagram',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1740076487/blaze_assets/mobile_home_and_create_new/social_media/instagram_yes0vt.png',
        level: 'instagram',
      },
      {
        title: 'Facebook',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1740076483/blaze_assets/mobile_home_and_create_new/social_media/facebook_swdlpj.png',
        level: 'facebook',
      },
      {
        title: 'LinkedIn',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1740076486/blaze_assets/mobile_home_and_create_new/social_media/linkedin_s20snn.png',
        level: 'linkedin',
      },
      {
        title: 'X/Twitter',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1740076485/blaze_assets/mobile_home_and_create_new/social_media/twitter_gnt3i0.png',
        level: 'twitter',
      },
    ],
    MARKETPLACE: [
      {
        title: 'Product Copy',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741289152/blaze_assets/mobile_home_and_create_new/L3/product-copy_v54jls.png',
        level: 'product_copy',
        textWidth: '50%',
      },
      {
        title: 'Product Visual',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1740075739/blaze_assets/mobile_home_and_create_new/create_new/product-visual_vdx2la.png',
        level: 'product_visual',
      },
    ],
    VIDEO: [
      {
        title: 'TikTok',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1740076482/blaze_assets/mobile_home_and_create_new/social_media/tiktok_i7aw7h.png',
        level: 'tiktok',
      },
      {
        title: 'YouTube',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1740076484/blaze_assets/mobile_home_and_create_new/social_media/youtube_a4rmfh.png',
        level: 'youtube',
      },
    ],
    BLOG_SEO: [
      {
        title: 'Blog Post Builder',
        description: 'Blaze offers a comprehensive blog post builder with keyword and seo analysis and outbound links.',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1740075742/blaze_assets/mobile_home_and_create_new/create_new/blog-post-builder_ibjxzz.png',
        wizard: ChannelType.BlogPost,
      },
    ],
    BRAINSTORM_IDEAS: [
      {
        title: 'Brainstorm Content Ideas',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741206198/blaze_assets/mobile_home_and_create_new/create_new/brainstorm-content-ideas-small_zvnwjk.png',
        wizard: GenerationType.Brainstorm,
      },
      {
        title: 'Content Plan',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1740075740/blaze_assets/mobile_home_and_create_new/create_new/content-plan_p5a5ie.png',
        wizard: GenerationType.ContentPlan,
      },
    ],
    START_BLANK: [
      {
        title: 'New Document',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741292067/blaze_assets/mobile_home_and_create_new/create_new/new-document_s7wg7x.png',
        level: 'new_document',
      },
      {
        title: 'New Design',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741292070/blaze_assets/mobile_home_and_create_new/create_new/new-design_ae0bzp.png',
        level: 'new_design',
        textWidth: '50%',
      },
    ],
    ADVANCED_TOOLS: [
      {
        title: 'Plan a Month of Content',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1740075741/blaze_assets/mobile_home_and_create_new/create_new/multi-channel-campaign_mpflug.png',
        wizard: GenerationType.ContentPlan,
      },
      {
        title: 'Multi Channel Campaign',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1740075743/blaze_assets/mobile_home_and_create_new/create_new/multi-blog-posts_vif42m.png',
        wizard: GenerationType.Campaign,
      },
      {
        title: 'Turn Anything into Content',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1740075738/blaze_assets/mobile_home_and_create_new/create_new/turninto_gpeuaz.png',
        level: 'turn_into',
      },
    ],
    AD_COPY: [
      {
        title: 'Generate Copy for Ads',
        description: 'Blaze supports the most popular social media and search platforms.',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1740075735/blaze_assets/mobile_home_and_create_new/create_new/ad-copy_fbesb4.png',
        level: 'ad_copy',
      },
    ],
    EMAILS: [
      {
        title: 'Newsletter',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1740075737/blaze_assets/mobile_home_and_create_new/create_new/newsletter_tdlgcb.png',
        level: 'newsletter',
      },
      {
        title: 'Lifecycle',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1740075734/blaze_assets/mobile_home_and_create_new/create_new/promo-email-sequence_omj7gt.png',
        level: 'lifecycle',
      },
      {
        title: 'Ecommerce',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1740075734/blaze_assets/mobile_home_and_create_new/create_new/promo-email-sequence_omj7gt.png',
        level: 'ecommerce',
      },
      {
        title: 'Sales Outbound',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1740075734/blaze_assets/mobile_home_and_create_new/create_new/promo-email-sequence_omj7gt.png',
        level: 'email_sales',
      },
    ],
    OTHER: [
      {
        title: 'Websites',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1740075736/blaze_assets/mobile_home_and_create_new/create_new/website_fok0ra.png',
        level: 'websites',
      },
      {
        title: 'Sales',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1740075732/blaze_assets/mobile_home_and_create_new/create_new/sales_ga2c4t.png',
        level: 'sales',
      },
      {
        title: 'Customer Support',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1740075733/blaze_assets/mobile_home_and_create_new/create_new/customer-support_pjgpyy.png',
        level: 'customer_support',
      },
      {
        title: 'Recruiting',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1740075732/blaze_assets/mobile_home_and_create_new/create_new/recruiting_ch8nud.png',
        level: 'recruiting',
      },
    ],
  },
  L2: {
    BLOG_SEO: [
      {
        wizard: ChannelType.BlogPost,
        title: 'Blog Post',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741203389/blaze_assets/mobile_home_and_create_new/L3/blog-post_roekio.png',
      },
      {
        wizard: ChannelType.BlogIdeas,
        title: 'Blog Ideas',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741203388/blaze_assets/mobile_home_and_create_new/L3/blog-ideas_mwzmnu.png',
      },
      {
        wizard: ChannelType.CaseStudy,
        title: 'Case Study',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741203396/blaze_assets/mobile_home_and_create_new/L3/case-study_x0zrhb.png',
      },
      {
        wizard: ChannelType.PressRelease,
        title: 'Press Release',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741203403/blaze_assets/mobile_home_and_create_new/L3/press-release_jmombm.png',
      },
      {
        wizard: GenerationType.Outline,
        title: 'Content Outline',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741203399/blaze_assets/mobile_home_and_create_new/L3/content-outline_k7cokx.png',
      },
      {
        wizard: GenerationType.ContentBrief,
        title: 'Content Brief',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741203397/blaze_assets/mobile_home_and_create_new/L3/content-brief_j8xm6a.png',
      },
      {
        wizard: GenerationType.SEOPlanning,
        title: 'Multiple Blog Posts',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741203401/blaze_assets/mobile_home_and_create_new/L3/multiple-blog-posts_yn3jwn.png',
      },
    ],
    BRAINSTORM_IDEAS: [
      {
        wizard: GenerationType.Brainstorm,
        title: 'Brainstorm Content Ideas',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741203392/blaze_assets/mobile_home_and_create_new/L3/brainstorm-content-ideas_snezfd.png',
      },
      {
        wizard: GenerationType.ContentPlan,
        title: 'Create a Content Plan',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741207981/blaze_assets/mobile_home_and_create_new/L3/create-a-content-plan_cicfay.png',
      },

      {
        wizard: ChannelType.BlogIdeas,
        title: 'Brainstorm Blog Ideas',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741208102/blaze_assets/mobile_home_and_create_new/L3/brainstorm-blog-ideas_oerzxq.png',
      },
      {
        wizard: ChannelType.NewsletterIdeas,
        title: 'Brainstorm Newsletter Ideas',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741208104/blaze_assets/mobile_home_and_create_new/L3/brainstorm-newsletter-ideas_jujrke.png',
      },
    ],
    MARKETPLACE: [
      {
        title: 'Product Copy',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741289152/blaze_assets/mobile_home_and_create_new/L3/product-copy_v54jls.png',
        level: 'product_copy',
        textWidth: '50%',
      },
      {
        title: 'Product Visual',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1740075739/blaze_assets/mobile_home_and_create_new/create_new/product-visual_vdx2la.png',
        level: 'product_visual',
      },
    ],
    SOCIAL_MEDIA: [
      {
        level: 'instagram',
        title: 'Instagram',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1740076487/blaze_assets/mobile_home_and_create_new/social_media/instagram_yes0vt.png',
      },
      {
        level: 'facebook',
        title: 'Facebook',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1740076483/blaze_assets/mobile_home_and_create_new/social_media/facebook_swdlpj.png',
      },
      {
        level: 'linkedin',
        title: 'LinkedIn',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1740076486/blaze_assets/mobile_home_and_create_new/social_media/linkedin_s20snn.png',
      },
      {
        level: 'tiktok',
        title: 'TikTok',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1740076482/blaze_assets/mobile_home_and_create_new/social_media/tiktok_i7aw7h.png',
      },
      {
        level: 'twitter',
        title: 'X/Twitter',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1740076485/blaze_assets/mobile_home_and_create_new/social_media/twitter_gnt3i0.png',
      },
      {
        level: 'youtube',
        title: 'YouTube',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1740076484/blaze_assets/mobile_home_and_create_new/social_media/youtube_a4rmfh.png',
      },
      {
        level: 'pinterest',
        title: 'Pinterest',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1740076480/blaze_assets/mobile_home_and_create_new/social_media/pinterest_d2mkpq.png',
      },
      {
        level: 'whatsapp',
        title: 'WhatsApp',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1740076480/blaze_assets/mobile_home_and_create_new/social_media/whatsapp_ngodzx.png',
      },
    ],
    VIDEO: [
      {
        level: 'tiktok',
        title: 'TikTok',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1740076482/blaze_assets/mobile_home_and_create_new/social_media/tiktok_i7aw7h.png',
      },
      {
        level: 'youtube',
        title: 'YouTube',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1740076484/blaze_assets/mobile_home_and_create_new/social_media/youtube_a4rmfh.png',
      },
      {
        level: 'instagram',
        title: 'Instagram',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1740076487/blaze_assets/mobile_home_and_create_new/social_media/instagram_yes0vt.png',
      },
      /*
      {
        level: 'facebook',
        title: 'Facebook',
        imgSrc: 'https://res.cloudinary.com/almanac/image/upload/v1740076483/blaze_assets/mobile_home_and_create_new/social_media/facebook_swdlpj.png',
      },
      {
        level: 'linkedin',
        title: 'LinkedIn',
        imgSrc: 'https://res.cloudinary.com/almanac/image/upload/v1740076486/blaze_assets/mobile_home_and_create_new/social_media/linkedin_s20snn.png',
      },
      {
        level: 'twitter',
        title: 'X/Twitter',
        imgSrc: 'https://res.cloudinary.com/almanac/image/upload/v1740076485/blaze_assets/mobile_home_and_create_new/social_media/twitter_gnt3i0.png',
      },
      */
    ],
  },
  L3: {
    AD_COPY: [
      {
        wizard: ChannelType.FacebookAds,
        title: 'Facebook Ad Copy',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1740076483/blaze_assets/mobile_home_and_create_new/social_media/facebook_swdlpj.png',
      },
      {
        wizard: ChannelType.LinkedInAds,
        title: 'LinkedIn Ad Copy',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1740076486/blaze_assets/mobile_home_and_create_new/social_media/linkedin_s20snn.png',
      },
      {
        wizard: ChannelType.TikTokAds,
        title: 'TikTok Ad Copy',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1740076482/blaze_assets/mobile_home_and_create_new/social_media/tiktok_i7aw7h.png',
      },
      {
        wizard: ChannelType.YoutubeAds,
        title: 'YouTube Ad Copy',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1740076484/blaze_assets/mobile_home_and_create_new/social_media/youtube_a4rmfh.png',
      },
      {
        wizard: ChannelType.PinterestAds,
        title: 'Pinterest Ad Copy',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1740076480/blaze_assets/mobile_home_and_create_new/social_media/pinterest_d2mkpq.png',
      },
      {
        wizard: ChannelType.GoogleAds,
        title: 'Google Ad Copy',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1740683852/blaze_assets/mobile_home_and_create_new/marketplace/google_etdgqj.png',
      },
      {
        wizard: ChannelType.BingAds,
        title: 'Bing Ad Copy',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741206133/blaze_assets/mobile_home_and_create_new/marketplace/bing_apy88u.png',
      },
      {
        wizard: ChannelType.AmazonAds,
        title: 'Amazon Ad Copy',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1740683852/blaze_assets/mobile_home_and_create_new/marketplace/amazon_xz5unk.png',
      },
    ],
    CUSTOMER_SUPPORT: [
      {
        wizard: ChannelType.ReplyTemplates,
        title: 'Customer Reply Templates',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741205500/blaze_assets/mobile_home_and_create_new/L3/customer-reply-templates_yxzorf.png',
      },
      {
        wizard: ChannelType.HelpDocs,
        title: 'Customer Help Docs',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741205498/blaze_assets/mobile_home_and_create_new/L3/customer-help-docs_bstavg.png',
      },
      {
        wizard: ChannelType.ReturnPolicy,
        title: 'Return Policy',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741205556/blaze_assets/mobile_home_and_create_new/L3/return-policy_jbtqmb.png',
      },
      {
        wizard: ChannelType.ShippingPolicy,
        title: 'Shipping Policy',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741205565/blaze_assets/mobile_home_and_create_new/L3/shipping-policy_aszamc.png',
      },
      {
        wizard: ChannelType.CareInstructions,
        title: 'Product Care Instructions',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741205553/blaze_assets/mobile_home_and_create_new/L3/product-care-instructions_pwjtpi.png',
      },
      {
        wizard: ChannelType.BillingAndPayments,
        title: 'Billing & Payments FAQs',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741205495/blaze_assets/mobile_home_and_create_new/L3/billing-and-payments-faq_opficv.png',
      },
      {
        wizard: ChannelType.PaymentConfirmation,
        title: 'Payment Confirmation Copy',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741205548/blaze_assets/mobile_home_and_create_new/L3/payment-confirmation-copy_oshv6g.png',
      },
    ],
    ECOMMERCE: [
      {
        wizard: ChannelType.DidntSignUp,
        title: "Didn't Sign Up Email",
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741204477/blaze_assets/mobile_home_and_create_new/L3/didnt-sign-up_wnvzqo.png',
      },
      {
        wizard: ChannelType.AbandonedCart,
        title: 'Abandoned Cart Email',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741204474/blaze_assets/mobile_home_and_create_new/L3/abandoned-cart_dpagar.png',
      },
      {
        wizard: ChannelType.LostCustomer,
        title: 'Lost Customer Email',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741204479/blaze_assets/mobile_home_and_create_new/L3/lost-customer_jxzvxo.png',
      },
      {
        wizard: ChannelType.SMSMessages,
        title: 'SMS Message Copy',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741204486/blaze_assets/mobile_home_and_create_new/L3/sms-message-copy_i2j4fi.png',
      },
    ],
    EMAIL_SALES: [
      {
        wizard: ChannelType.WarmOutboundEmailSequence,
        title: 'Warm Outbound Email Sequence',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741204487/blaze_assets/mobile_home_and_create_new/L3/warm-outbound-email_upbspm.png',
      },
      {
        wizard: ChannelType.ColdOutboundEmailSequence,
        title: 'Cold Outbound Email Sequence',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741204476/blaze_assets/mobile_home_and_create_new/L3/cold-outbound-email_olrakk.png',
      },
    ],
    FACEBOOK: [
      {
        wizard: DesignChannel.FacebookPost,
        title: 'Facebook Post',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741201583/blaze_assets/mobile_home_and_create_new/L3/facebook-post_dgpxvi.png',
      },
      /*
      {
        title: 'Facebook Story',
        imgSrc: '',
      },
      {
        title: 'Facebook Reel',
        imgSrc: '',
      },
      */
      {
        wizard: DesignChannel.FacebookCover,
        title: 'Facebook Cover Image',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741201583/blaze_assets/mobile_home_and_create_new/L3/facebook-cover-image_ujprbu.png',
      },
      {
        wizard: DesignChannel.FacebookProfilePicture,
        title: 'Facebook Profile Picture',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1742843333/blaze_assets/mobile_home_and_create_new/L3/facebook-profile-picture_ubwvot.png',
        imgScale: 0.55,
        textWidth: '60%',
      },
      {
        wizard: GenerationType.FacebookPlanning,
        title: 'Multiple Facebook Posts',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741712854/blaze_assets/mobile_home_and_create_new/L3/multiple-facebook-posts_xp3mp7.png',
      },
      /*
      {
        title: 'Facebook Captions',
        imgSrc: '',
      },
      {
        title: 'Facebook Reel Script',
        imgSrc: '',
      },
      */
    ],
    INSTAGRAM: [
      {
        wizard: DesignChannel.InstagramPost,
        title: 'Instagram Post',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741201008/blaze_assets/mobile_home_and_create_new/L3/instagram-post_lfvxau.png',
      },
      {
        wizard: DesignChannel.InstagramStory,
        title: 'Instagram Story',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741201585/blaze_assets/mobile_home_and_create_new/L3/instagram-story_xrl8oz.png',
      },
      {
        wizard: DesignChannel.InstagramReel,
        title: 'Instagram Reel',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741201585/blaze_assets/mobile_home_and_create_new/L3/instagram-reel_obm7vu.png',
      },
      {
        wizard: GenerationType.InstagramPlanning,
        title: 'Multiple Instagram Posts',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741712596/blaze_assets/mobile_home_and_create_new/L3/multiple-instagram-posts_eqa3d1.png',
        imgScale: 0.55,
      },
      /*
      {
        title: 'Instagram Captions',
        imgSrc: '',
      },
      */
      {
        wizard: ChannelType.InstagramReel,
        title: 'Instagram Reel Script',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741201584/blaze_assets/mobile_home_and_create_new/L3/instagram-reel-script_bk6ylb.png',
      },
    ],
    LIFECYCLE: [
      {
        wizard: ChannelType.PromotionalEmailSequence,
        title: 'Promotional Email Sequence',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741204484/blaze_assets/mobile_home_and_create_new/L3/promotional-email-sequence_ziujfp.png',
      },
      {
        wizard: ChannelType.NurtureEmailSequence,
        title: 'Nurture Sequence',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741204483/blaze_assets/mobile_home_and_create_new/L3/nurture-sequence_yxcc5j.png',
      },
      {
        wizard: ChannelType.WelcomeSeries,
        title: 'Welcome Series',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741204488/blaze_assets/mobile_home_and_create_new/L3/welcome-series_tvoipb.png',
      },
    ],
    LINKEDIN: [
      {
        level: 'linkedin_post',
        title: 'LinkedIn Post',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741201586/blaze_assets/mobile_home_and_create_new/L3/linkedin-post_g7cytj.png',
        textWidth: '60%',
      },
      {
        wizard: DesignChannel.LinkedInProfilePicture,
        title: 'LinkedIn Profile Picture',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1742843333/blaze_assets/mobile_home_and_create_new/L3/linkedin-profile-picture_lmy2mw.png',
        imgScale: 0.55,
        textWidth: '60%',
      },
      {
        wizard: DesignChannel.LinkedInBanner,
        title: 'LinkedIn Banner',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1742843333/blaze_assets/mobile_home_and_create_new/L3/linkedin-banner_gpor4a.png',
        imgScale: 0.55,
      },
      {
        wizard: GenerationType.LinkedInPlanning,
        title: 'Multiple LinkedIn Posts',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741712854/blaze_assets/mobile_home_and_create_new/L3/multiple-facebook-posts_xp3mp7.png',
      },
      /*
      {
        title: 'LinkedIn Captions',
        imgSrc: '',
      },
      */
    ],
    NEWSLETTER: [
      {
        wizard: ChannelType.Newsletter,
        title: 'Newsletter Copy',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741204480/blaze_assets/mobile_home_and_create_new/L3/newsletter-copy_pr3gnj.png',
      },
      {
        wizard: ChannelType.NewsletterIdeas,
        title: 'Newsletter Ideas',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741204481/blaze_assets/mobile_home_and_create_new/L3/newsletter-ideas_qszhjo.png',
      },
    ],
    PINTEREST: [
      {
        wizard: DesignChannel.Pinterest,
        title: 'Pinterest Product Visual',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741201584/blaze_assets/mobile_home_and_create_new/L3/pinterest-product-visual_nenigh.png',
      },
    ],
    PRODUCT_COPY: [
      {
        wizard: ChannelType.ProductLaunchWebsite,
        title: 'Your Website',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1740075739/blaze_assets/mobile_home_and_create_new/create_new/product-visual_vdx2la.png',
      },
      {
        wizard: ChannelType.ProductLaunchGoogle,
        title: 'Google Shop',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1740683852/blaze_assets/mobile_home_and_create_new/marketplace/google_etdgqj.png',
      },
      /*
      {
        title: 'eBay',
        imgSrc: '',
      },
      {
        title: 'Poshmark',
        imgSrc: '',
      },
      {
        title: 'Depop',
        imgSrc: '',
      },
      {
        title: 'Mercari',
        imgSrc: '',
      },
      {
        title: 'MercadoLibre',
        imgSrc: '',
      },
      {
        title: 'Shopee',
        imgSrc: '',
      },
      {
        title: 'Lazada',
        imgSrc: '',
      },
      */
      {
        wizard: ChannelType.ProductLaunchEtsy,
        title: 'Etsy',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1740687052/blaze_assets/mobile_home_and_create_new/marketplace/etsy_ucruku.png',
      },
      /*
      {
        title: 'Vinted',
        imgSrc: '',
      },
      */
      {
        wizard: ChannelType.ProductLaunchAmazon,
        title: 'Amazon',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1740683852/blaze_assets/mobile_home_and_create_new/marketplace/amazon_xz5unk.png',
      },
      {
        wizard: ChannelType.ProductLaunchShopify,
        title: 'Shopify',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1740687693/blaze_assets/mobile_home_and_create_new/marketplace/shopify_a18u44.png',
      },
    ],
    PRODUCT_VISUAL: [
      {
        wizard: 'vinted',
        title: 'Vinted',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1740687692/blaze_assets/mobile_home_and_create_new/marketplace/vinted_d4e2vq.png',
      },
      {
        wizard: 'ebay',
        title: 'eBay',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1740687691/blaze_assets/mobile_home_and_create_new/marketplace/ebay_j3oroq.png',
      },
      {
        wizard: 'poshmark',
        title: 'Poshmark',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1740687691/blaze_assets/mobile_home_and_create_new/marketplace/poshmark_vjp62j.png',
      },
      {
        wizard: 'depop',
        title: 'Depop',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1740687690/blaze_assets/mobile_home_and_create_new/marketplace/depop_oe7bxz.png',
      },
      {
        wizard: 'mercari',
        title: 'Mercari',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1740687693/blaze_assets/mobile_home_and_create_new/marketplace/mercari_ncmpbt.png',
      },
      {
        wizard: 'mercadolibre',
        title: 'MercadoLibre',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1740687691/blaze_assets/mobile_home_and_create_new/marketplace/mercado-libre_elihkg.png',
      },
      {
        wizard: 'shopee',
        title: 'Shopee',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1740687692/blaze_assets/mobile_home_and_create_new/marketplace/shopee_gwwyvt.png',
      },
      {
        wizard: 'lazada',
        title: 'Lazada',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1740687692/blaze_assets/mobile_home_and_create_new/marketplace/lazada_guiqz3.png',
      },
      {
        wizard: 'etsy',
        title: 'Etsy',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1740687052/blaze_assets/mobile_home_and_create_new/marketplace/etsy_ucruku.png',
      },
      {
        wizard: 'amazon',
        title: 'Amazon',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1740683852/blaze_assets/mobile_home_and_create_new/marketplace/amazon_xz5unk.png',
      },
    ],
    RECRUITING: [
      {
        wizard: ChannelType.JobDescription,
        title: 'Job Description Copy',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741205505/blaze_assets/mobile_home_and_create_new/L3/job-description-copy_t2h0ej.png',
      },
      {
        wizard: ChannelType.NurtureEmailSequence,
        title: 'Candidate Nurture Email Sequence',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741205497/blaze_assets/mobile_home_and_create_new/L3/candidate-nurture-email-sequence_ptfe0a.png',
      },
    ],
    SALES: [
      {
        wizard: ChannelType.OnePager,
        title: 'Sales One Pager',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741205562/blaze_assets/mobile_home_and_create_new/L3/sales-one-pager_l3s5fw.png',
      },
      {
        wizard: ChannelType.ProductDemo,
        title: 'Product Demo Script',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741205562/blaze_assets/mobile_home_and_create_new/L3/sales-one-pager_l3s5fw.png',
      },
      {
        wizard: ChannelType.PreCallChecklist,
        title: 'Pre-call Checklist',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741205549/blaze_assets/mobile_home_and_create_new/L3/pre-call-checklist_m73vre.png',
      },
      {
        wizard: ChannelType.CallScript,
        title: 'Sales Call Script',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741205560/blaze_assets/mobile_home_and_create_new/L3/sales-call-script_qbzlfj.png',
      },
      {
        wizard: ChannelType.Research,
        title: 'Customer Research Brief',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741205503/blaze_assets/mobile_home_and_create_new/L3/customer-research-brief_aq4k9h.png',
      },
      {
        wizard: ChannelType.BattleCard,
        title: 'Sales Battle Card',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741205558/blaze_assets/mobile_home_and_create_new/L3/sales-battle-card_f3vlmv.png',
      },
      {
        wizard: ChannelType.Testimonial,
        title: 'Sales Testimonial',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741205563/blaze_assets/mobile_home_and_create_new/L3/sales-testimonial_jz1zyv.png',
      },
    ],
    TIKTOK: [
      {
        wizard: DesignChannel.TikTokPost,
        title: 'TikTok Post',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741201590/blaze_assets/mobile_home_and_create_new/L3/tiktok-post_jvlmtn.png',
      },
      {
        wizard: ChannelType.TikTokScript,
        title: 'TikTok Script',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741201588/blaze_assets/mobile_home_and_create_new/L3/tiktok-script_ucp24p.png',
      },
      {
        wizard: DesignChannel.TikTokThumbnail,
        title: 'TikTok Thumbnail',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741201591/blaze_assets/mobile_home_and_create_new/L3/tiktok-thumbnail_hnhs0r.png',
      },
    ],
    TURN_INTO: [
      {
        wizard: TurnInto.VideoIntoContent,
        title: 'Turn Video into Content',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741204063/blaze_assets/mobile_home_and_create_new/L3/turn-video-into-content_fluudn.png',
      },
      {
        wizard: TurnInto.AudioIntoContent,
        title: 'Turn Audio into Content',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741204060/blaze_assets/mobile_home_and_create_new/L3/turn-audio-into-content_mtyu3q.png',
      },
      {
        wizard: TurnInto.WebsitesIntoContent,
        title: 'Turn a Website into Content',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741204065/blaze_assets/mobile_home_and_create_new/L3/turn-website-into-content_jyvuem.png',
      },
      {
        wizard: TurnInto.DocsIntoContent,
        title: 'Turn Blaze Doc into Content',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741204062/blaze_assets/mobile_home_and_create_new/L3/turn-blaze-doc-into-content_uq3qbu.png',
      },
    ],
    TWITTER: [
      {
        level: 'twitter_post',
        title: 'X/Twitter Post',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741201587/blaze_assets/mobile_home_and_create_new/L3/twitter-post_gfgxgh.png',
      },
      {
        wizard: ChannelType.TweetThread,
        title: 'X/Twitter Thread',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741201588/blaze_assets/mobile_home_and_create_new/L3/twitter-thread_beuclo.png',
      },
      {
        wizard: DesignChannel.TwitterProfilePicture,
        title: 'X/Twitter Profile Picture',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1742843333/blaze_assets/mobile_home_and_create_new/L3/twitter-profile-picture_fk02zx.png',
      },
      {
        wizard: DesignChannel.TwitterCoverImage,
        title: 'X/Twitter Cover Image',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1742843334/blaze_assets/mobile_home_and_create_new/L3/twitter-cover-image_pbv89s.png',
      },
      {
        wizard: GenerationType.TwitterPlanning,
        title: 'Multiple X/Twitter Posts',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741712997/blaze_assets/mobile_home_and_create_new/L3/multiple-twitter-posts_wfxlfy.png',
      },
    ],
    WEBSITES: [
      {
        wizard: ChannelType.LandingPage,
        title: 'Landing Page',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741205508/blaze_assets/mobile_home_and_create_new/L3/landing-page_cmiris.png',
      },
      {
        wizard: ChannelType.AboutUs,
        title: 'About Us Page',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741205493/blaze_assets/mobile_home_and_create_new/L3/about-us-page_ny0tik.png',
      },
      {
        wizard: ChannelType.Pricing,
        title: 'Pricing Page',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741205551/blaze_assets/mobile_home_and_create_new/L3/pricing-page_bs35zs.png',
      },
      {
        wizard: ChannelType.FAQs,
        title: 'FAQ Page',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741205501/blaze_assets/mobile_home_and_create_new/L3/faq-page_py0b1u.png',
      },
      {
        wizard: ChannelType.Mission,
        title: 'Mission Page',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741205509/blaze_assets/mobile_home_and_create_new/L3/mission-page_yeudsu.png',
      },
      {
        wizard: ChannelType.Impact,
        title: 'Impact Page',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741205506/blaze_assets/mobile_home_and_create_new/L3/impact-page_tg2tw7.png',
      },
    ],
    WHATSAPP: [
      {
        wizard: ChannelType.SMSMessages,
        title: 'WhatsApp Message Copy',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741201588/blaze_assets/mobile_home_and_create_new/L3/whatsapp-message-copy_xprlbc.png',
      },
      {
        wizard: DesignChannel.WhatsAppSticker,
        title: 'WhatsApp Sticker',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741201589/blaze_assets/mobile_home_and_create_new/L3/whatsapp-sticker_m0qrif.png',
      },
    ],
    YOUTUBE: [
      {
        wizard: DesignChannel.YoutubeShort,
        title: 'YouTube Short',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741201584/blaze_assets/mobile_home_and_create_new/L3/youtube-script_haghog.png',
      },
      {
        wizard: ChannelType.YoutubeScript,
        title: 'YouTube Script',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741201584/blaze_assets/mobile_home_and_create_new/L3/youtube-script_haghog.png',
      },
      {
        wizard: DesignChannel.YoutubeProfilePicture,
        title: 'YouTube Profile Picture',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1742843333/blaze_assets/mobile_home_and_create_new/L3/youtube-profile-picture_krb0k9.png',
        imgScale: 0.5,
      },
      {
        wizard: DesignChannel.YoutubeChannelArt,
        title: 'YouTube Channel Art',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1742843334/blaze_assets/mobile_home_and_create_new/L3/youtube-channel-art_dhqbm6.png',
        imgScale: 0.5,
      },
    ],
  },
  L4: {
    LINKEDIN_POST_OPTIONS: [
      {
        wizard: ChannelType.LinkedInPosts,
        title: 'Mostly text',
        description: 'with a simple photo or video',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741201586/blaze_assets/mobile_home_and_create_new/L3/linkedin-post_g7cytj.png',
        textWidth: '50%',
        forYouLevel: 'linkedin_post',
      },
      {
        wizard: DesignChannel.LinkedInPost,
        title: 'More visual',
        description: 'with a custom designed template',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741201586/blaze_assets/mobile_home_and_create_new/L3/linkedin-more-visual_juemm7.png',
        textWidth: '50%',
        forYouLevel: 'linkedin_post',
      },
    ],
    TWITTER_POST_OPTIONS: [
      {
        wizard: ChannelType.Tweets,
        title: 'Mostly text',
        description: 'with a simple photo or video',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741201586/blaze_assets/mobile_home_and_create_new/L3/linkedin-post_g7cytj.png',
        textWidth: '50%',
        forYouLevel: 'twitter_post',
      },
      {
        wizard: DesignChannel.TwitterPost,
        title: 'More visual',
        description: 'with a custom designed template',
        imgSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1741201586/blaze_assets/mobile_home_and_create_new/L3/linkedin-more-visual_juemm7.png',
        textWidth: '50%',
        forYouLevel: 'twitter_post',
      },
    ],
  },
} as const as Levels

export const getCardMaps = () => {
  const allCards = Object.values(LEVEL_CARDS).flatMap((level) => Object.values(level).flat())
  const wizardCardMap: Record<string, WizardCard> = {}
  const levelCardMap: Partial<LevelCardMap> = {}

  allCards.forEach((card) => {
    if (card.wizard) {
      wizardCardMap[card.wizard as Wizard] = card
    }

    if (card.level) {
      levelCardMap[card.level] = card
    }
  })

  return {
    wizardCardMap: wizardCardMap as WizardCardMap,
    levelCardMap: levelCardMap as LevelCardMap,
  }
}
